import { useState, useCallback, useRef } from 'react';
import { CopilotContext } from './context';
import { CopilotContextProps, CopilotContextData, DomainCopilotMap, CopilotScope } from './types';
import { createCompositeKey } from './constants';

/**
 * Copilot Context Provider Component
 *
 * This provider maintains a map of domains to their Copilot context data.
 * It provides functions to get and set data for specific domains and keys.
 */
const CopilotProvider = ({ children }: CopilotContextProps) => {
  // State to store domain-specific Copilot data
  const [domainMap, setDomainMap] = useState<DomainCopilotMap>({});
  // Reference to store values by composite key
  const refsMap = useRef<Record<string, any>>({});

  // Get full data for a specific domain and feature
  const getDomainData = useCallback(
    (scope: CopilotScope): CopilotContextData | undefined => {
      const key = createCompositeKey(scope.domain, scope.feature);
      return domainMap[key];
    },
    [domainMap]
  );

  // Set full data for a specific domain and feature
  const setDomainData = useCallback((scope: CopilotScope, data: CopilotContextData) => {
    const key = createCompositeKey(scope.domain, scope.feature);
    setDomainMap(prevMap => ({
      ...prevMap,
      [key]: data,
    }));
  }, []);

  // Get a specific key from a domain and feature's data
  const getKey = useCallback(
    <T,>(scope: CopilotScope, key: keyof CopilotContextData): T | undefined => {
      const compositeKey = createCompositeKey(scope.domain, scope.feature);
      const domainData = domainMap[compositeKey];
      if (!domainData) return undefined;
      return (domainData[key] as unknown) as T;
    },
    [domainMap]
  );

  // Set a specific key for a domain and feature
  const setKey = useCallback(<T,>(scope: CopilotScope, key: keyof CopilotContextData, value: T) => {
    const compositeKey = createCompositeKey(scope.domain, scope.feature);
    setDomainMap(prevMap => {
      // If domain+feature doesn't exist yet, create it with default empty values
      const currentDomainData = prevMap[compositeKey] || {
        sessionId: '',
        sessionMode: '',
        conversation: { history: [] },
        query: '',
        autoExecute: false,
        ref: null,
        locationId: '',
        channelType: '',
        openCopilot: false, // Default value for openCopilot flag
      };

      return {
        ...prevMap,
        [compositeKey]: {
          ...currentDomainData,
          [key]: value,
        },
      };
    });
  }, []);

  // Set the entire domain map
  const setEntireDomainMap = useCallback((map: DomainCopilotMap) => {
    setDomainMap(map);
  }, []);

  // Get ref for a specific domain and feature
  const getRef = useCallback((scope: CopilotScope): any | null | undefined => {
    const compositeKey = createCompositeKey(scope.domain, scope.feature);
    return refsMap.current[compositeKey];
  }, []);

  // Set ref for a specific domain and feature
  const setRef = useCallback((scope: CopilotScope, value: any): void => {
    const compositeKey = createCompositeKey(scope.domain, scope.feature);
    refsMap.current[compositeKey] = value;
  }, []);

  const contextValue = {
    domainMap,
    getDomainData,
    setDomainData,
    getKey,
    setKey,
    getRef,
    setRef,
    setDomainMap: setEntireDomainMap,
  };

  return <CopilotContext.Provider value={contextValue}>{children}</CopilotContext.Provider>;
};

export default CopilotProvider;
